import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import GoogleDriveForm from '@/components/GoogleDriveForm';
import { useI18n } from '@/hooks/useI18n';

interface CostCenter {
  id: string;
  name: string;
  address?: string;
  keywords?: string;
  company_id?: string;
  is_default: boolean;
  is_active: boolean;
}

interface FormData {
  name: string;
  address: string;
  keywords: string;
  company_id: string;
  is_default: boolean;
  is_active: boolean;
}

interface Company {
  id: string;
  name: string;
  tax_id?: string;
  is_active: boolean;
}

interface CashRegisterDevice {
  id: string;
  cost_center_id: string;
  ap_number: string;
  name?: string;
  is_active: boolean;
  generate_orders?: boolean;
  credentials_cost_center_id?: string | null;
  last_file_date?: string | null;
  last_processed_file_number?: number | null;
}

interface SyncStatusResponse {
  id: string;
  status: string;
  current_step?: string | null;
  result_summary?: string | null;
  error_message?: string | null;
  sync_metadata?: Record<string, unknown> | null;
}

export default function CostCenterDetailPage() {
  const navigate = useNavigate();
  const params = useParams({ from: '/cost-centers/$id' });
  const routerState = useRouterState();
  const searchParams = routerState.location.search as any;
  const { id } = params;
  const { tab } = searchParams || {};
  const isNew = id === 'new';
  const { t } = useI18n();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [costCenter, setCostCenter] = useState<CostCenter | null>(null);
  const [formData, setFormData] = useState<FormData>({ name: '', address: '', keywords: '', company_id: '', is_default: false, is_active: true });

  // Companies dropdown
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);

  // Cash registers tab state
  const [devices, setDevices] = useState<CashRegisterDevice[]>([]);
  const [newDeviceAP, setNewDeviceAP] = useState('');
  const [newDeviceName, setNewDeviceName] = useState('');
  const [newDeviceGenerateOrders, setNewDeviceGenerateOrders] = useState(false);

  // NAV credentials list for per-device selection
  type NavCredItem = { id: string; cost_center_id: string; cost_center_name: string; tax_number: string; is_active: boolean };
  const [navCreds, setNavCreds] = useState<NavCredItem[]>([]);
  const loadNavCreds = async () => {
    const res = await apiFetch('/api/nav-credentials/?include_inactive=false');
    if (res.ok) {
      const data = await res.json();
      setNavCreds(data || []);
    }
  };

  const [syncing, setSyncing] = useState(false);
  const [syncSummary, setSyncSummary] = useState<string | null>(null);
  const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const buildSyncSummary = (syncStatus: SyncStatusResponse): string | null => {
    const metadata = syncStatus.sync_metadata || {};
    const devices = metadata['devices'];
    const filesDownloaded = metadata['files_downloaded'];
    const processed = metadata['processed'];
    const failed = metadata['failed'];
    const remaining = metadata['remaining'];

    const hasSummaryNumbers = [
      devices,
      filesDownloaded,
      processed,
      failed,
      remaining,
    ].every((item) => typeof item === 'number');

    if (hasSummaryNumbers) {
      const devicesCount = devices as number;
      const filesDownloadedCount = filesDownloaded as number;
      const processedCount = processed as number;
      const failedCount = failed as number;
      const remainingCount = remaining as number;
      return [
        `Devices: ${devicesCount}`,
        `Files downloaded: ${filesDownloadedCount}`,
        `Processed: ${processedCount}`,
        `Failed: ${failedCount}`,
        `Remaining: ${remainingCount}`,
      ].join(' · ');
    }

    return syncStatus.result_summary || null;
  };

  const pollSyncStatus = async (syncId: string, costCenterId: string) => {
    const maxAttempts = 600; // 30 minutes at 3s interval
    const intervalMs = 3000;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const statusRes = await apiFetch(`/api/sync-status/${syncId}`);
      if (!statusRes.ok) {
        await sleep(intervalMs);
        continue;
      }

      const syncStatus: SyncStatusResponse = await statusRes.json();
      const statusValue = (syncStatus.status || '').toLowerCase();

      if (statusValue === 'failed' || statusValue === 'cancelled') {
        throw new Error(syncStatus.error_message || t('costCenters.syncFailed', 'common'));
      }

      if (statusValue === 'completed' || statusValue === 'partial') {
        const summary = buildSyncSummary(syncStatus);
        if (summary) {
          setSyncSummary(summary);
        }
        await loadDevices(costCenterId);
        return;
      }

      setSyncSummary(syncStatus.current_step || t('costCenters.cashRegisters.syncing', 'common'));
      await sleep(intervalMs);
    }

    throw new Error('Sync is still running in the background. Please refresh in a minute.');
  };

  const syncNow = async () => {
    if (!id) return;
    try {
      setSyncing(true);
      setSyncSummary(null);
      const res = await apiFetch(`/api/nav-cash-register/sync/start?cost_center_id=${id}`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({} as any));
        alert(err?.detail || t('costCenters.syncFailed', 'common'));
        return;
      }
      const data = await res.json().catch(() => null);
      const syncId = data?.sync_id;
      if (!syncId) {
        throw new Error('Sync started but no sync ID was returned.');
      }
      setSyncSummary(data?.message || t('costCenters.cashRegisters.syncing', 'common'));
      await pollSyncStatus(syncId, id);
    } catch (e) {
      console.error('Sync failed', e);
      const message = e instanceof Error && e.message ? e.message : t('costCenters.syncFailed', 'common');
      alert(message);
    } finally {
      setSyncing(false);
    }
  };


  const activeTab = useMemo(() => (typeof tab === 'string' && tab ? tab : 'settings'), [tab]);

  const loadCompanies = async () => {
    try {
      const res = await apiFetch('/api/companies/?include_inactive=false');
      if (res.ok) {
        const data = await res.json();
        setCompanies(data || []);
        setCompaniesLoaded(true);
      }
    } catch (e) {
      console.error('Failed to load companies', e);
      setCompaniesLoaded(true); // Set to true even on error to avoid blocking UI
    }
  };

  // Load companies once on mount
  useEffect(() => {
    loadCompanies();
  }, []);

  // Load cost center data when id changes
  useEffect(() => {
    if (isNew) {
      setLoading(false);
      return;
    }
    if (!id) return;
    loadCostCenter(id);
    loadDevices(id);
    loadNavCreds();
  }, [id, isNew]);

  const loadCostCenter = async (costCenterId: string) => {
    try {
      setLoading(true);
      const res = await apiFetch(`/api/cost-centers/${costCenterId}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || t('costCenters.loadFailed', 'common'));
        return;
      }
      const data: CostCenter = await res.json();
      setCostCenter(data);
      setFormData({
        name: data.name,
        address: data.address || '',
        keywords: data.keywords || '',
        company_id: data.company_id || '',
        is_default: !!data.is_default,
        is_active: data.is_active,
      });
    } catch (e) {
      setError(t('costCenters.loadFailed', 'common'));
    } finally {
      setLoading(false);
    }
  };

  async function loadDevices(costCenterId: string) {
    try {
      const res = await apiFetch(`/api/nav-cash-register/devices/?cost_center_id=${costCenterId}`);
      if (!res.ok) return;
      const data: CashRegisterDevice[] = await res.json();
      setDevices(data);
    } catch {}
  }

  const handleBack = () => navigate({ to: '/cost-centers'});

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (isNew) {
        const res = await apiFetch('/api/cost-centers/', { method: 'POST', body: JSON.stringify(formData) });
        if (res.ok) {
          const created = await res.json();
          navigate({ to: `/cost-centers/${created.id}`, replace: true });
          return;
        }
        const err = await res.json().catch(() => ({}));
        setError(err.detail || t('costCenters.createFailed', 'common'));
      } else if (id) {
        const res = await apiFetch(`/api/cost-centers/${id}`, { method: 'PATCH', body: JSON.stringify(formData) });
        if (res.ok) {
          const updated = await res.json();
          setCostCenter(updated);
        } else {
          const err = await res.json().catch(() => ({}));
          setError(err.detail || t('costCenters.saveFailed', 'common'));
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteCostCenter = async () => {
    if (!id) return;
    if (!confirm(t('costCenters.deleteConfirm', 'common'))) return;
    const res = await apiFetch(`/api/cost-centers/${id}`, { method: 'DELETE' });
    if (res.ok) navigate({ to: '/cost-centers'});
  };

  // Cash register actions
  const addDevice = async () => {
    if (!id) return;
    if (!newDeviceAP.trim()) { alert(t('costCenters.cashRegisters.apRequired', 'common')); return; }
    const res = await apiFetch('/api/nav-cash-register/devices/', {
      method: 'POST',
      body: JSON.stringify({
        cost_center_id: id,
        ap_number: newDeviceAP.trim(),
        name: newDeviceName.trim() || undefined,
        is_active: true,
        generate_orders: newDeviceGenerateOrders,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.detail || t('costCenters.cashRegisters.addFailed', 'common'));
      return;
    }
    setNewDeviceAP('');
    setNewDeviceName('');
    setNewDeviceGenerateOrders(false);
    loadDevices(id);
  };

  const updateDevice = async (deviceId: string, patch: Partial<CashRegisterDevice>) => {
    await apiFetch(`/api/nav-cash-register/devices/${deviceId}`, { method: 'PATCH', body: JSON.stringify(patch) });
    if (id) loadDevices(id);
  };

  const removeDevice = async (deviceId: string) => {
    if (!confirm(t('costCenters.cashRegisters.removeConfirm', 'common'))) return;
    await apiFetch(`/api/nav-cash-register/devices/${deviceId}`, { method: 'DELETE' });
    if (id) loadDevices(id);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={handleBack} className="h-8 w-8 p-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {isNew ? t('costCenters.createTitle', 'common') : t('costCenters.editTitle', 'common', { name: costCenter?.name || '' })}
            </h1>
            <p className="text-muted-foreground">
              {isNew ? t('costCenters.createSubtitle', 'common') : t('costCenters.editSubtitle', 'common')}
            </p>
          </div>
          {!isNew && (
            <div className="ml-auto">
              <Button variant="outline" className="text-destructive hover:text-destructive/90" onClick={deleteCostCenter}>
                <Trash2 className="h-4 w-4 mr-1" /> {t('actions.delete', 'common')}
              </Button>
            </div>
          )}
        </div>

        {error && (
          <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
        )}

        <Tabs defaultValue={activeTab} className="w-full">
          <TabsList>
            <TabsTrigger value="settings">{t('costCenters.tabs.settings', 'common')}</TabsTrigger>
            {!isNew && <TabsTrigger value="gdrive">{t('costCenters.tabs.gdrive', 'common')}</TabsTrigger>}
            {!isNew && <TabsTrigger value="cash-registers">{t('costCenters.tabs.cashRegisters', 'common')}</TabsTrigger>}
          </TabsList>

          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>{t('costCenters.settingsTitle', 'common')}</CardTitle>
                <CardDescription>{t('costCenters.settingsDescription', 'common')}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveSettings} className="space-y-6">
                  <div className="grid gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="name">{t('costCenters.fields.name', 'common')}</Label>
                      <Input id="name" value={formData.name} onChange={(e)=>setFormData({ ...formData, name: e.target.value })} required />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="address">{t('costCenters.fields.address', 'common')}</Label>
                      <Input id="address" value={formData.address} onChange={(e)=>setFormData({ ...formData, address: e.target.value })} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="keywords">{t('costCenters.fields.keywords', 'common')}</Label>
                      <Input id="keywords" value={formData.keywords} onChange={(e)=>setFormData({ ...formData, keywords: e.target.value })} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="company_id">{t('costCenters.fields.company', 'common')}</Label>
                      {companiesLoaded ? (
                        <Select
                          value={formData.company_id || 'none'}
                          onValueChange={(value) => setFormData({ ...formData, company_id: value === 'none' ? '' : value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('costCenters.selectCompanyOptional', 'common')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem key="none" value="none">{t('costCenters.noCompany', 'common')}</SelectItem>
                            {companies.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input value={t('costCenters.loadingCompanies', 'common')} disabled />
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="is_default" checked={formData.is_default} onCheckedChange={(checked)=>setFormData({ ...formData, is_default: checked as boolean })} />
                      <Label htmlFor="is_default">Default (fallback)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="is_active" checked={formData.is_active} onCheckedChange={(checked)=>setFormData({ ...formData, is_active: checked as boolean })} />
                      <Label htmlFor="is_active">{t('status.active', 'common')}</Label>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-4">
                    <Button type="button" variant="outline" onClick={handleBack} disabled={saving}>{t('actions.cancel', 'common')}</Button>
                    <Button type="submit" disabled={saving}>{saving ? t('actions.saving', 'common') : (isNew ? t('actions.create', 'common') : t('actions.saveChanges', 'common'))}</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {!isNew && (
            <TabsContent value="gdrive">
              <Card>
                <CardHeader>
                  <CardTitle>{t('gdrive.integration', 'common')}</CardTitle>
                  <CardDescription>{t('costCenters.gdriveDescription', 'common')}</CardDescription>
                </CardHeader>
                <CardContent>
                  {costCenter && (
                    <GoogleDriveForm costCenterId={costCenter.id} onCredentialsChange={()=>{ /* no-op */ }} />
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {!isNew && (
            <TabsContent value="cash-registers">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <CardTitle>{t('costCenters.cashRegisters.title', 'common')}</CardTitle>
                      <CardDescription>{t('costCenters.cashRegisters.description', 'common')}</CardDescription>
                    </div>
                    <Button onClick={syncNow} disabled={syncing}>
                      {syncing ? t('costCenters.cashRegisters.syncing', 'common') : t('costCenters.cashRegisters.syncNow', 'common')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {syncSummary && (
                    <Alert>
                      <AlertDescription>{syncSummary}</AlertDescription>
                    </Alert>
                  )}
                  <div className="flex items-end space-x-2">
                    <div className="flex-1">
                      <Label htmlFor="ap_number">{t('costCenters.cashRegisters.apNumber', 'common')}</Label>
                      <Input id="ap_number" value={newDeviceAP} onChange={(e)=>setNewDeviceAP(e.target.value)} placeholder={t('costCenters.cashRegisters.apPlaceholder', 'common')} />
                    </div>
                    <div className="flex-1">
                      <Label htmlFor="device_name">{t('costCenters.cashRegisters.nameOptional', 'common')}</Label>
                      <Input id="device_name" value={newDeviceName} onChange={(e)=>setNewDeviceName(e.target.value)} placeholder={t('costCenters.cashRegisters.namePlaceholder', 'common')} />
                    </div>
                    <label className="text-sm flex items-center space-x-2 pb-2">
                      <input
                        type="checkbox"
                        checked={newDeviceGenerateOrders}
                        onChange={(e)=>setNewDeviceGenerateOrders(e.currentTarget.checked)}
                      />
                      <span>{t('costCenters.cashRegisters.generateOrders', 'common')}</span>
                    </label>
                    <Button onClick={addDevice}>{t('actions.addNew', 'common')}</Button>
                  </div>

                  <div className="space-y-2">
                    {devices.length === 0 ? (
                      <div className="text-sm text-muted-foreground">{t('costCenters.cashRegisters.noDevices', 'common')}</div>
                    ) : (
                      devices.map((d) => (
                        <div key={d.id} className="flex items-center justify-between rounded border p-2">
                          <div className="flex-1">
                            <div className="text-sm font-medium">{d.ap_number}</div>
                            <div className="text-xs text-muted-foreground">
                              {t('costCenters.cashRegisters.last', 'common')} {d.last_file_date ? new Date(d.last_file_date).toLocaleString() : t('costCenters.cashRegisters.never', 'common')}
                            </div>
                          </div>
                          <div className="flex-1 px-2">
                            <div className="flex items-center gap-2">
                              <Input
                                defaultValue={d.name || ''}
                                placeholder={t('costCenters.cashRegisters.name', 'common')}
                                onBlur={(e)=>updateDevice(d.id, { name: e.currentTarget.value })}
                              />
                              <select
                                className="border rounded px-2 py-1 text-xs"
                                value={d.credentials_cost_center_id ?? ''}
                                onChange={async (e)=>{
                                  const val = e.currentTarget.value || null;
                                  await updateDevice(d.id, { credentials_cost_center_id: val });
                                  if (id) await loadDevices(id);
                                }}
                                title={t('costCenters.cashRegisters.navCredentialsFrom', 'common')}
                              >
                                <option key="this-cost-center" value="">{t('costCenters.cashRegisters.thisCostCenter', 'common')}</option>
                                {navCreds.map((c) => (
                                  <option key={c.id} value={c.cost_center_id}>
                                    {c.cost_center_name} ({c.tax_number})
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <label className="text-sm flex items-center space-x-1">
                              <input
                                type="checkbox"
                                defaultChecked={!!d.generate_orders}
                                onChange={(e)=>updateDevice(d.id, { generate_orders: e.currentTarget.checked })}
                              />
                              <span>{t('costCenters.cashRegisters.generateOrders', 'common')}</span>
                            </label>
                            <label className="text-sm flex items-center space-x-1">
                              <input type="checkbox" defaultChecked={d.is_active} onChange={(e)=>updateDevice(d.id, { is_active: e.currentTarget.checked })} />
                              <span>{t('status.active', 'common')}</span>
                            </label>
                            <Button variant="outline" className="text-destructive hover:text-destructive/90" onClick={()=>removeDevice(d.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
}

export const Route = createFileRoute('/cost-centers/$id')({
  component: CostCenterDetailPage,
})
